import * as Headless from '@headlessui/react';
import { Form } from 'formik';
import { motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import { ComponentPropsWithoutRef, memo, MouseEvent, ReactNode, useCallback, useRef } from 'react';

import { fontLevelClass, Text } from '@components';
import { DisplayMode } from '@enums';
import { useElementScrollLock } from '@hooks';
import { cn, validateValueEqual } from '@utils';

export const modalSizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
};

export const DialogModal = memo(
  ({
    onClose,
    size = 'lg',
    className,
    displayMode = DisplayMode.Light,
    notForm = false,
    withoutPadding = false,
    open = false,
    children,
    ...props
  }: {
    size?: keyof typeof modalSizes;
    className?: string;
    children: ReactNode;
    withoutPadding?: boolean;
    notForm?: boolean;
    displayMode?: DisplayMode;
    open: boolean;
    onClose: () => void;
  } & Omit<Headless.DialogProps, 'className'>) => {
    useElementScrollLock('mainContainer', open);
    const dialogRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<number>(0);

    const isLightTheme = validateValueEqual(displayMode, DisplayMode.Light);
    const handleBackdropClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;

        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) return;

        const clickDuration = Date.now() - startTimeRef.current;
        if (clickDuration > 200) return;

        onClose();
      },
      [onClose],
    );

    const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        startTimeRef.current = Date.now();
      }
    }, []);
    const debouncedOnClose = useCallback(
      debounce((value) => {
        onClose(value);
      }, 100),
      [onClose],
    );
    return (
      <Headless.Dialog
        ref={dialogRef}
        open={open}
        onClose={(value) => {
          const isClickOutside = !dialogRef.current?.contains(document.activeElement);
          if (isClickOutside) {
            debouncedOnClose(value);
          }
        }}
        className="fixed inset-0 z-40 overflow-y-auto"
        {...props}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 bg-zinc-950/25 dark:bg-zinc-950/50 backdrop-blur-[2px] will-change-opacity"
        />

        <div className="fixed inset-0 w-screen overflow-y-auto padded-scrollbar-track pt-6 sm:pt-0">
          <motion.div
            className={cn(
              'min-h-full flex justify-center sm:p-8',
              notForm ? 'px-6 pb-6 items-center' : 'items-end sm:items-center',
            )}
            initial={{ translateY: '100%' }}
            animate={{ translateY: '0%' }}
            exit={{ translateY: '100%' }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            onClick={handleBackdropClick}
            onMouseDown={handleMouseDown}
            style={{ pointerEvents: 'auto' }}
          >
            <div
              className={cn(
                modalSizes[size],
                'relative row-start-2 w-full min-w-0 forced-colors:outline ring-1',
                isLightTheme
                  ? 'bg-white dark:bg-zinc-900 shadow-card ring-zinc-950/10 dark:ring-white/10'
                  : 'bg-theme-neutral-dark shadow-glow ring-white/10',
                notForm ? 'rounded-3xl' : 'rounded-t-3xl sm:rounded-3xl',
                !withoutPadding && 'p-8 pt-10 sm:pt-8',
                className,
              )}
            >
              {children}
            </div>
          </motion.div>
        </div>
      </Headless.Dialog>
    );
  },
);

export function ModalTitle({
  className,
  level = 2,
  ...props
}: { className?: string; level?: number } & Omit<Headless.DialogTitleProps, 'className'>) {
  return (
    <Headless.DialogTitle
      {...props}
      className={cn(
        fontLevelClass[level],
        'text-balance font-medium text-zinc-950 sm:text-base/6 dark:text-white leading-5',
        className,
      )}
    />
  );
}

export function ModalDescription({
  className,
  $level = 5,
  ...props
}: {
  className?: string;
  level?: number;
} & Omit<Headless.DescriptionProps<typeof Text>, 'className'>) {
  return (
    <Headless.Description
      {...props}
      as={Text}
      $level={$level}
      className={cn($level && fontLevelClass[$level], ' mt-4 sm:mt-3 text-pretty leading-2', className)}
    />
  );
}

type DivProps = ComponentPropsWithoutRef<'div'>;
type FormProps = ComponentPropsWithoutRef<'form'>;

interface ModalBodyProps extends DivProps {
  notForm?: boolean;
}

export function ModalBody({ className, notForm = false, ...props }: Readonly<ModalBodyProps>) {
  const commonClassName = cn('gap-6 sm:gap-5 flex flex-col', className);

  if (notForm) {
    return <div {...props} className={commonClassName} />;
  }

  return <Form {...(props as FormProps)} className={commonClassName} />;
}

export function ModalActions({ className, ...props }: Readonly<ComponentPropsWithoutRef<'div'>>) {
  return (
    <div {...props} className={cn('mt-3 flex flex-row items-center justify-end gap-7 sm:flex-row w-auto', className)} />
  );
}
